import React, { useState, useEffect, useRef, useCallback } from 'react';
import firebaseApp from '../Firecontact';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import { toast } from 'react-toastify';
import Header from '../Header';
import Footer from '../Footer';
import pdfBundleImg from '../assets/image/pdf_bundle.png';
import OurClients from '../OurClients';
import Testimonial from '../Testimonial';


// Custom Interactive Before/After Image Slider
const BeforeAfterSlider = ({ before, after }) => {
  const [sliderPos, setSliderPos] = useState(50);

  return (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', bgcolor: '#0b1121' }}>
      {/* After Image (AI Generated) */}
      <img src={after} alt="After" style={{ width: '100%', height: 'auto', display: 'block', backgroundColor: '#0b1121', objectFit: 'contain' }} />
      <Chip label="After AI" size="small" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: '500', zIndex: 3, bgcolor: '#25d366', color: '#fff' }} />

      {/* Before Image with Clip-Path */}
      <img
        src={before}
        alt="Before"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#0b1121', // Prevents After image from leaking behind Before
          clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`,
          display: 'block'
        }}
      />
      <Chip label="Before" size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: '500', zIndex: 3, bgcolor: '#ffc107', color: '#131921' }} />

      {/* Vertical divider line */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${sliderPos}%`,
        width: '3px',
        bgcolor: '#ffffff',
        zIndex: 4,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        boxShadow: '0 0 10px rgba(0,0,0,0.5)'
      }}>
        {/* Handle circle */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          border: '2.5px solid #3a7bd5'
        }}>
          <span style={{ color: '#3a7bd5', fontWeight: 'bold', fontSize: '12px' }}>
            ◀▶
          </span>
        </Box>
      </Box>

      {/* Invisible Range Input spanning the container */}
      <input
        type="range"
        min="0"
        max="100"
        value={sliderPos}
        onChange={(e) => setSliderPos(e.target.value)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 5,
          cursor: 'ew-resize',
          margin: 0
        }}
      />
    </Box>
  );
};

// ૫. PDF Bundle Purchase Helper
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PdfProductBox = ({ product, initialOpenDemo = false }) => {
  const [buyerMobile, setBuyerMobile] = useState('');
  const [buyerBusiness, setBuyerBusiness] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openDemoModal, setOpenDemoModal] = useState(false);

  useEffect(() => {
    if (initialOpenDemo) {
      setOpenDemoModal(true);
      setTimeout(() => {
        const element = document.getElementById(`product-${product.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [initialOpenDemo, product.id]);

  useEffect(() => {
    if (openModal || openDemoModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openModal, openDemoModal]);

  useEffect(() => {
    const savedMobile = localStorage.getItem('buyerMobile');
    const savedBusiness = localStorage.getItem('buyerBusiness');
    if (savedMobile) setBuyerMobile(savedMobile);
    if (savedBusiness) setBuyerBusiness(savedBusiness);

    const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
    if (savedPurchases.includes(product.id)) {
      if (savedMobile) {
        const verifyPurchase = async () => {
          try {
            const db = firebaseApp.firestore();
            const existingPurchase = await db.collection('pdf_purchases')
              .where('mobile', '==', savedMobile)
              .where('product', '==', product.id)
              .get();

            let hasValidPurchase = false;
            existingPurchase.forEach(doc => {
              if (doc.data().paymentId && doc.data().paymentId !== 'N/A') {
                hasValidPurchase = true;
              }
            });

            if (!hasValidPurchase) {
              setPurchased(false);
              const updatedPurchases = savedPurchases.filter(id => id !== product.id);
              localStorage.setItem('purchasedPdfs', JSON.stringify(updatedPurchases));
            } else {
              setPurchased(true);
            }
          } catch (error) {
            console.error("Verification error", error);
            setPurchased(true); // Fallback if offline
          }
        };
        verifyPurchase();
      } else {
        setPurchased(false);
        const updatedPurchases = savedPurchases.filter(id => id !== product.id);
        localStorage.setItem('purchasedPdfs', JSON.stringify(updatedPurchases));
      }
    }
  }, [product.id]);

  const triggerDownload = async () => {
    const url = product.pdfUrl;
    if (!url) {
      toast.error("PDF file not available.");
      return;
    }
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${product.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!buyerMobile || !buyerBusiness) {
      toast.error("Please fill in both mobile number and business name.");
      return;
    }

    setPurchasing(true);

    try {
      const db = firebaseApp.firestore();
      const existingPurchase = await db.collection('pdf_purchases')
        .where('mobile', '==', buyerMobile)
        .where('product', '==', product.id)
        .get();

      let hasPaid = false;
      existingPurchase.forEach(doc => {
        if (doc.data().paymentId && doc.data().paymentId !== 'N/A') {
          hasPaid = true;
        }
      });

      if (hasPaid) {
        setPurchased(true);
        setOpenModal(false);

        const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
        localStorage.setItem('purchasedPdfs', JSON.stringify([...new Set([...savedPurchases, product.id])]));
        localStorage.setItem('buyerMobile', buyerMobile);
        localStorage.setItem('buyerBusiness', buyerBusiness);

        toast.success(`Welcome back ${buyerBusiness}! You already purchased this PDF. You can download it now.`);
        setPurchasing(false);
        triggerDownload();
        return;
      }
    } catch (error) {
      console.error("Error checking existing purchase: ", error);
    }

    const res = await loadRazorpayScript();
    if (!res) {
      toast.error("Razorpay failed to load. Check your connection.");
      setPurchasing(false);
      return;
    }

    let pendingDocId = null;
    try {
      const db = firebaseApp.firestore();
      const docRef = await db.collection('pdf_purchases').add({
        product: product.id,
        mobile: buyerMobile,
        businessName: buyerBusiness,
        paymentId: 'N/A',
        amount: 299,
        timestamp: new Date()
      });
      pendingDocId = docRef.id;
    } catch (error) {
      console.error("Error saving lead data: ", error);
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_RGylGTr37qW531",
      amount: "29900", // 299.00 INR
      currency: "INR",
      name: "9R AI Studio",
      description: `${product.title} (PDF)`,
      handler: async function (response) {
        try {
          const db = firebaseApp.firestore();
          if (pendingDocId) {
            await db.collection('pdf_purchases').doc(pendingDocId).update({
              paymentId: response.razorpay_payment_id,
              timestamp: new Date()
            });
          } else {
            await db.collection('pdf_purchases').add({
              product: product.id,
              mobile: buyerMobile,
              businessName: buyerBusiness,
              paymentId: response.razorpay_payment_id,
              amount: 299,
              timestamp: new Date()
            });
          }
          setPurchased(true);
          setOpenModal(false);

          const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
          localStorage.setItem('purchasedPdfs', JSON.stringify([...new Set([...savedPurchases, product.id])]));
          localStorage.setItem('buyerMobile', buyerMobile);
          localStorage.setItem('buyerBusiness', buyerBusiness);

          toast.success("Payment successful! Your PDF is downloading...");
          triggerDownload();
        } catch (error) {
          console.error("Error saving purchase: ", error);
          toast.error("Payment received but error saving details. Please contact support.");
        }
      },
      prefill: {
        contact: buyerMobile,
      },
      theme: {
        color: "#0b1121"
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on('payment.failed', function (response) {
      toast.error("Payment Failed. Please try again.");
    });
    paymentObject.open();
    setPurchasing(false);
  };

  const handleShareProduct = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const shareUrl = `${window.location.origin}${window.location.pathname}?product=${product.id}`;

    if (navigator.share) {
      navigator.share({
        title: product.title || "9R Premium AI Prompt Bundle",
        text: product.desc || "Check out this amazing premium AI prompt bundle!",
        url: shareUrl
      })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            navigator.clipboard.writeText(shareUrl);
            toast.success("Premium product link copied to clipboard!");
          }
        });
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          toast.success("Premium product link copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy product link: ", err);
          toast.error("Failed to copy link.");
        });
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: 4,
      overflow: 'hidden',
      height: '100%',
      position: 'relative'
    }}>
      <Box sx={{ position: 'absolute', top: '-20%', left: '-20%', width: '140%', height: '140%', background: 'radial-gradient(circle at center, rgba(232,255,0,0.03) 0%, transparent 60%)', zIndex: 0 }} />

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
          boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.05)',
          position: 'relative'
        }}>
          <img src={product.img} alt={product.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
          {/* Floating Share Button */}
          <IconButton
            onClick={handleShareProduct}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              bgcolor: '#e8ff00',
              color: '#0b1121',
              border: '1px solid #e8ff00',
              p: 1,
              '&:hover': {
                bgcolor: '#ffffff',
                color: '#0b1121',
                borderColor: '#ffffff',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
            size="small"
          >
            <ShareIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Chip label="PREMIUM AI PROMPTS" size="small" sx={{ bgcolor: 'rgba(232, 255, 0, 0.1)', color: '#e8ff00', fontWeight: 'bold', border: '1px solid rgba(232, 255, 0, 0.2)', fontSize: '0.7rem' }} />
          <Button
            size="small"
            onClick={() => setOpenDemoModal(true)}
            sx={{
              color: '#fff',
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              px: 1.5,
              border: '1px solid rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            View Demo <VisibilityIcon sx={{ ml: 0.5, fontSize: '16px' }} />
          </Button>
        </Box>

        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1, fontSize: '20px' }}>
          {product.title}
        </Typography>

        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, fontSize: '14px', lineHeight: 1.5 }}>
          {product.desc}
        </Typography>

        <Box sx={{ mt: 'auto' }}>
          {!purchased ? (
            <Button
              fullWidth
              onClick={() => setOpenModal(true)}
              sx={{
                mt: 1,
                py: 1.2,
                bgcolor: '#e8ff00',
                color: '#0b1121',
                fontWeight: 800,
                fontSize: '15px',
                '&:hover': { bgcolor: '#c8eb00' },
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              Buy Now - ₹299
            </Button>
          ) : (
            <Button
              fullWidth
              onClick={triggerDownload}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                mt: 1,
                py: 1.2,
                bgcolor: '#25d366',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px',
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#21b858', boxShadow: 'none' }
              }}
            >
              Download PDF
            </Button>
          )}
        </Box>
      </Box>

      {/* Checkout Modal */}
      <Dialog
        open={openModal}
        onClose={() => !purchasing && setOpenModal(false)}
        disableScrollLock={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(11, 17, 33, 0.8)',
            backdropFilter: 'blur(12px)'
          }
        }}
        PaperProps={{
          sx: {
            bgcolor: '#131929',
            color: '#fff',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 2, fontSize: '18px', fontWeight: 600 }}>
          Checkout - {product.title}
        </DialogTitle>
        <form onSubmit={handlePurchase}>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontSize: '14px' }}>
              Please enter your details to proceed with the payment of ₹299.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mobile Number"
                  variant="outlined"
                  required
                  value={buyerMobile}
                  onChange={(e) => setBuyerMobile(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: '14px' },
                    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#e8ff00' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e8ff00' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #0b1121 inset !important',
                      WebkitTextFillColor: '#ffffff !important',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Business Name"
                  variant="outlined"
                  required
                  value={buyerBusiness}
                  onChange={(e) => setBuyerBusiness(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: '14px' },
                    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#e8ff00' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e8ff00' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #0b1121 inset !important',
                      WebkitTextFillColor: '#ffffff !important',
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <Button
              onClick={() => setOpenModal(false)}
              disabled={purchasing}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={purchasing}
              sx={{
                bgcolor: '#e8ff00',
                color: '#0b1121',
                fontWeight: 600,
                '&:hover': { bgcolor: '#c8eb00' },
                '&.Mui-disabled': {
                  bgcolor: '#e8ff00 !important',
                  color: 'rgba(11, 17, 33, 0.7) !important',
                  opacity: 0.8
                },
                textTransform: 'none'
              }}
            >
              {purchasing ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} sx={{ color: '#0b1121' }} />
                  <span>Processing...</span>
                </Box>
              ) : (
                'Proceed to Pay'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Demo Modal */}
      <Dialog
        open={openDemoModal}
        onClose={() => setOpenDemoModal(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(11, 17, 33, 0.8)',
            backdropFilter: 'blur(12px)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            bgcolor: '#0b1121',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
            m: { xs: 1.5, sm: 2 },
            width: { xs: 'calc(100% - 24px)', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'sm' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 1.5, pt: 2, px: 2.5, fontSize: { xs: '15px', sm: '17px' }, fontWeight: 600, fontFamily: '"Poppins", sans-serif' }}>
          <span>Demo - {product.title}</span>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton
              onClick={handleShareProduct}
              sx={{
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.1)',
                p: 0.8,
                '&:hover': {
                  color: '#e8ff00',
                  borderColor: '#e8ff00',
                  bgcolor: 'rgba(232, 255, 0, 0.05)'
                }
              }}
              size="small"
            >
              <ShareIcon sx={{ fontSize: '15px' }} />
            </IconButton>
            <Button onClick={() => setOpenDemoModal(false)} sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 'auto', p: 0.5, fontSize: '18px' }}>✕</Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 1.5, overflowY: 'auto', maxHeight: '70vh', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.02)', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '4px' } }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mt: 2, mb: 3, fontSize: '0.85rem', lineHeight: 1.6, fontFamily: '"Poppins", sans-serif' }}>
            {product.desc}
          </Typography>
          {product.demos && product.demos.length > 0 ? (
            product.demos.map((demo, index) => (
              <Box key={index} sx={{ mb: index !== product.demos.length - 1 ? 3 : 0 }}>
                <Box sx={{ position: 'relative', width: '100%', height: 'auto', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)' }}>
                  <BeforeAfterSlider before={demo.beforeImg} after={demo.afterImg} />
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: '600', mb: 0.5, fontSize: '0.85rem', fontFamily: '"Poppins", sans-serif' }}>
                  AI Prompt:
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'rgba(0,0,0,0.3)',
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    color: '#e8ff00',
                    whiteSpace: 'pre-wrap',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {demo.prompt}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Demo images for this PDF will be available soon.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.05)', gap: 1.5 }}>
          {!purchased ? (
            <Button
              fullWidth
              onClick={() => {
                setOpenDemoModal(false);
                setOpenModal(true);
              }}
              sx={{
                py: 1.2,
                bgcolor: '#e8ff00',
                color: '#0b1121',
                fontWeight: 800,
                fontSize: '15px',
                '&:hover': { bgcolor: '#c8eb00' },
                textTransform: 'none',
                borderRadius: 2,
                fontFamily: '"Poppins", sans-serif'
              }}
            >
              Buy Now - ₹299
            </Button>
          ) : (
            <Button
              fullWidth
              onClick={triggerDownload}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                py: 1.2,
                bgcolor: '#25d366',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px',
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#21b858', boxShadow: 'none' },
                fontFamily: '"Poppins", sans-serif'
              }}
            >
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default function AiTrendsGallery() {
  const [trends, setTrends] = useState([]);
  const [filteredTrends, setFilteredTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [pdfProducts, setPdfProducts] = useState([]);
  const [filteredPdfProducts, setFilteredPdfProducts] = useState([]);
  const [selectedPdfCategory, setSelectedPdfCategory] = useState('All');
  const [loadingPdfs, setLoadingPdfs] = useState(true);
  const [activeProductId, setActiveProductId] = useState(null);

  const getProductCategory = useCallback((product) => {
    if (product.category) return product.category;
    const title = product.title || '';
    // dynamic cleaning: remove common wrapper words and keep the core name
    let clean = title.replace(/(Luxury|Premium|AI|Prompts?|Photography|Photographies|Bundles?|Products?|Books?)/gi, '').trim();
    // Capitalize first letter and safeguard against any standalone 'S'
    clean = clean.split(' ').filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .filter(w => w !== 'S')
      .join(' ').trim();
    return clean || 'Premium';
  }, []);

  const getPdfCategories = useCallback(() => {
    const cats = pdfProducts.map(p => getProductCategory(p));
    return ['All', ...new Set(cats.filter(Boolean))];
  }, [pdfProducts, getProductCategory]);

  // ---- Ad Interstitial States ----
  const [pendingTrend, setPendingTrend] = useState(null);
  const [adCountdown, setAdCountdown] = useState(0);
  const adTimerRef = useRef(null);
  const adRef = useRef(null);

  // Ad Interstitial: Start countdown & push AdSense when pendingTrend is set
  useEffect(() => {
    if (pendingTrend) {
      setAdCountdown(5);

      // Try to push the AdSense ad
      try {
        if (window.adsbygoogle && adRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (e) {
        // AdSense may not be ready on localhost
      }

      adTimerRef.current = setInterval(() => {
        setAdCountdown(prev => {
          if (prev <= 1) {
            clearInterval(adTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (adTimerRef.current) clearInterval(adTimerRef.current);
      };
    }
  }, [pendingTrend]);

  // When countdown reaches 0, show the actual prompt modal
  useEffect(() => {
    if (adCountdown === 0 && pendingTrend) {
      setSelectedTrend(pendingTrend);
      setPendingTrend(null);
    }
  }, [adCountdown, pendingTrend]);

  // Handler: Clicking "Get the AI Prompt" starts ad flow
  const handleGetPrompt = useCallback((trend) => {
    setPendingTrend(trend);
  }, []);

  // Skip ad manually
  const handleSkipAd = useCallback(() => {
    if (adTimerRef.current) clearInterval(adTimerRef.current);
    setAdCountdown(0);
  }, []);

  // ૧. ફાયરબેઝ ફાયરસ્ટોરમાંથી ડેટા ઇન્ડેક્સ-ફ્રી ફેચ કરવાનો કોડ
  useEffect(() => {
    const fetchAiTrends = async () => {
      setLoading(true);
      try {
        const db = firebaseApp.firestore();

        // ડાયરેક્ટ આખી કલેક્શન ગેટ કરો (જેથી કમ્પોઝિટ ઇન્ડેક્સ એરર ન આવે)
        const snapshot = await db.collection('ai_trends').get();
        const allData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // ક્લાયન્ટ સાઇડ પર જ એક્ટિવ સ્ટેટસ ફિલ્ટર કરો અને ઓર્ડર વાઇઝ સોર્ટ કરો
        const activeTrends = allData
          .filter(item => item.status === 'active')
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

        setTrends(activeTrends);
        setFilteredTrends(activeTrends);
      } catch (error) {
        console.error("Error fetching AI Trends on user side: ", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPdfProducts = async () => {
      try {
        const db = firebaseApp.firestore();
        const snapshot = await db.collection('ai_pdf_products').get();
        const fetchedPdfs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort by createdAt or title if needed
        fetchedPdfs.sort((a, b) => a.title.localeCompare(b.title));
        setPdfProducts(fetchedPdfs);
      } catch (error) {
        console.error("Error fetching PDF Products: ", error);
      } finally {
        setLoadingPdfs(false);
      }
    };

    fetchAiTrends();
    fetchPdfProducts();
  }, []);

  // ૨. સર્ચબાર અને કેટેગરી ફિલ્ટર લોજિક
  useEffect(() => {
    const filtered = trends.filter(trend => {
      const matchesSearch = trend.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (trend.category && trend.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (trend.shortDescription && trend.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'All' || trend.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
    setFilteredTrends(filtered);
  }, [searchQuery, selectedCategory, trends]);

  // ૨.૧. PDF પ્રોડક્ટ્સ ફિલ્ટર લોજિક
  useEffect(() => {
    const filtered = pdfProducts.filter(product => {
      const category = getProductCategory(product);
      const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.desc && product.desc.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedPdfCategory === 'All' || category === selectedPdfCategory;
      
      return matchesSearch && matchesCategory;
    });
    setFilteredPdfProducts(filtered);
  }, [searchQuery, selectedPdfCategory, pdfProducts, getProductCategory]);

  // ૩. Body Scroll Lock જ્યારે મોડલ ઓપન હોય
  useEffect(() => {
    if (selectedTrend) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        const savedScrollY = document.body.style.top;

        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        document.body.style.overflow = "";

        window.scrollTo(0, parseInt(savedScrollY || "0") * -1);
      };
    }
  }, [selectedTrend]);

  // ૪. પ્રોમ્પ્ટ કોપી કરવાનું ફંક્શન
  const handleCopyPrompt = (id, promptText) => {
    // HTML ટેગ્સ વગર પ્લેન ટેક્સ્ટ કોપી કરવા માટે
    const plainText = (promptText || '').replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(plainText);
    setCopiedId(id);
    toast.success("Prompt copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };



  useEffect(() => {
    if (pdfProducts.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const productId = params.get('product');
      if (productId) {
        setActiveProductId(productId);
      }
    }
  }, [pdfProducts]);

  if (loading || loadingPdfs) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121'
      }}>
        <Header name="AiContentStudio" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={50} sx={{ color: '#e8ff00' }} />
        </Box>
        <Footer name="AiContentStudio" />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      fontFamily: '"Poppins", sans-serif',
      background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121'
    }}>
      <Header name="AiContentStudio" />
      <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 6, md: 8 } }}>
        <div className="container">
          {/* હેડિંગ વિભાગ */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={{
              color: '#ffffff',
              mb: 2,
              fontSize: { xs: '28px', sm: '40px', md: '52px' },
              lineHeight: 1.1,
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600
            }}>
              Elevate Your Brand with <br />
              <span style={{ color: '#e8ff00' }}>9R AI Content Studio.</span>
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto', mb: 4, fontSize: { xs: '15px', sm: '18px', md: '20px' }, color: 'rgba(255,255,255,0.7)', fontWeight: 300, fontFamily: '"Poppins", sans-serif' }}>
              Discover expertly crafted AI prompts and tools to generate professional product photography, marketing assets, and stunning visuals for your business effortlessly.
            </Typography>

            {/* યુનિફાઈડ સર્ચ બાર - પેજ ની સૌથી ઉપર */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Search templates, premium books, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#3a7bd5' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 50,
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0px 3px 20px rgba(0, 0, 0, 0.2)',
                    fontFamily: '"Poppins", sans-serif',
                    '& fieldset': { border: '1px solid rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { border: '1px solid rgba(255,255,255,0.3)' },
                    '&.Mui-focused fieldset': { border: '1px solid #e8ff00' },
                    '& input': { color: '#ffffff', py: 2 }
                  }
                }}
                sx={{ maxWidth: 600, input: { color: '#ffffff' } }}
              />
            </Box>
          </Box>

          {/* Dynamic PDF Products Section */}
          {pdfProducts.length > 0 && getPdfCategories().length > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flexWrap: 'wrap', 
              gap: 1.5, 
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              px: 2
            }}>
              {getPdfCategories().map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  onClick={() => setSelectedPdfCategory(cat)}
                  clickable
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1,
                    py: 2,
                    borderRadius: '50px',
                    bgcolor: selectedPdfCategory === cat ? '#e8ff00' : 'rgba(255, 255, 255, 0.05)',
                    color: selectedPdfCategory === cat ? '#0b1121' : '#ffffff',
                    border: '1px solid',
                    borderColor: selectedPdfCategory === cat ? '#e8ff00' : 'rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: selectedPdfCategory === cat ? '#d4e600' : 'rgba(255, 255, 255, 0.12)',
                      borderColor: selectedPdfCategory === cat ? '#d4e600' : 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ mb: 6, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 2, sm: 2, xl: 3 } }}>
            {filteredPdfProducts.length > 0 ? filteredPdfProducts.map((product) => (
              <Box key={product.id} id={`product-${product.id}`} sx={{
                display: 'flex',
                width: '100%',
                '@media (min-width: 768px)': {
                  width: 'calc(50% - 16px)'
                },
                '@media (min-width: 992px)': {
                  width: 'calc(33.333% - 16px)'
                }
              }}>
                <PdfProductBox product={{
                  ...product,
                  // fallback to pdfBundleImg if img is empty
                  img: product.img || pdfBundleImg,
                  // prefix process.env.PUBLIC_URL only if pdfUrl is a local relative path, 
                  // else use directly (if it's a Cloudinary link)
                  pdfUrl: product.pdfUrl?.startsWith('http') ? product.pdfUrl : (product.pdfUrl || '')
                }}
                  initialOpenDemo={activeProductId === product.id}
                />
              </Box>
            )) : (
              <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)' }}>No premium AI products match your selection.</Typography>
              </Box>
            )}
          </Box>

          {/* AI Templates Section Header */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 8 }}>
            <Typography sx={{
              color: '#ffffff',
              fontWeight: '600',
              fontSize: { xs: '24px', sm: '32px' },
              mb: 1.5,
              fontFamily: '"Poppins", sans-serif'
            }}>
              Interactive <span style={{ color: '#e8ff00' }}>AI Prompt Templates</span>
            </Typography>
            <Typography variant="body2" sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              maxWidth: '650px',
              mx: 'auto',
              fontSize: { xs: '13px', sm: '15px' },
              fontWeight: 300,
              fontFamily: '"Poppins", sans-serif',
              lineHeight: 1.6
            }}>
              Slide to compare the "Before" real product and the "After AI" professional studio results. Reveal and copy the exact optimized prompts used to generate these premium visuals.
            </Typography>
          </Box>

          {/* કેટેગરી ફિલ્ટર ચિપ્સ */}
          {trends.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flexWrap: 'wrap', 
              gap: 1.5, 
              mb: 6,
              maxWidth: 800,
              mx: 'auto',
              px: 2
            }}>
              {['All', ...new Set(trends.map(t => t.category).filter(Boolean))].map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  onClick={() => setSelectedCategory(cat)}
                  clickable
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1,
                    py: 2,
                    borderRadius: '50px',
                    bgcolor: selectedCategory === cat ? '#e8ff00' : 'rgba(255, 255, 255, 0.05)',
                    color: selectedCategory === cat ? '#0b1121' : '#ffffff',
                    border: '1px solid',
                    borderColor: selectedCategory === cat ? '#e8ff00' : 'rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: selectedCategory === cat ? '#d4e600' : 'rgba(255, 255, 255, 0.12)',
                      borderColor: selectedCategory === cat ? '#d4e600' : 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          <Box sx={{
            width: '100%',
            columnCount: 1,
            columnGap: { xs: 2, sm: 2, xl: 3 },
            '@media (min-width: 768px)': {
              columnCount: 2
            },
            '@media (min-width: 992px)': {
              columnCount: 3
            },
            '& > div': {
              breakInside: 'avoid',
              marginBottom: { xs: 2, sm: 2, xl: 3 },
              display: 'inline-block',
              width: '100%'
            }
          }}>
            {filteredTrends.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, width: '100%', columnSpan: 'all' }}>
                <Typography variant="h6" sx={{ color: '#646163', fontFamily: '"Poppins", sans-serif' }}>No active AI trends found.</Typography>
              </Box>
            ) : (
              filteredTrends.map((trend) => (
                <Box key={trend.id}>
                  <Card sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(20px)',
                    transition: 'transform 0.4s ease, border-color 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: 'rgba(232, 255, 0, 0.3)'
                    }
                  }}>
                    {/* Before / After Image Slider */}
                    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#f5f5f5', width: '100%' }}>
                      {trend.beforeImageUrl && trend.afterImageUrl ? (
                        <Box sx={{ width: '100%', height: 'auto', '& img': { width: '100%', height: 'auto', display: 'block' } }}>
                          <BeforeAfterSlider before={trend.beforeImageUrl} after={trend.afterImageUrl} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 'auto', position: 'relative' }}>
                          <img src={trend.afterImageUrl || trend.beforeImageUrl} alt={trend.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                          <Chip label={trend.afterImageUrl ? "After AI" : "Before"} size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: '500', bgcolor: trend.afterImageUrl ? '#25d366' : '#ffc107', color: trend.afterImageUrl ? '#fff' : '#131921' }} />
                        </Box>
                      )}


                      {/* Trending / Featured badges */}
                      <Box sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 1, zIndex: 10 }}>
                        {trend.isTrending && <Chip label="Trending 🔥" size="small" sx={{ fontWeight: '600', height: 22, fontSize: '0.7rem', bgcolor: '#ff4d4f', color: '#fff' }} />}
                        {trend.isFeatured && <Chip label="Pro ⭐" size="small" sx={{ fontWeight: '700', height: 22, fontSize: '0.7rem', bgcolor: '#e8ff00', color: '#0b1121' }} />}
                      </Box>
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', px: 2, pt: 1.5, pb: '16px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#e8ff00', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1, fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem' }}>
                          {trend.category}
                        </Typography>
                      </Box>

                      <Typography variant="h6" sx={{ mb: 0.5, color: '#ffffff', fontWeight: '600', fontSize: '1rem', lineClamp: 1, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: '"Poppins", sans-serif' }}>
                        {trend.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.6)', fontWeight: 300, fontSize: '0.75rem', flexGrow: 1, lineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: '"Poppins", sans-serif' }}>
                        {trend.shortDescription}
                      </Typography>

                      {/* Actions */}
                      <Box sx={{ mt: 'auto' }}>
                        <Button
                          fullWidth
                          size="small"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => handleGetPrompt(trend)}
                          sx={{
                            borderRadius: 50,
                            fontWeight: '600',
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontFamily: '"Poppins", sans-serif',
                            color: '#0b1121',
                            bgcolor: '#e8ff00',
                            py: 0.8,
                            '&:hover': {
                              bgcolor: '#d4e600',
                              transform: 'scale(1.02)'
                            }
                          }}
                        >
                          Get the AI Prompt →
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))
            )}
          </Box>

          {/* ---- Ad Interstitial Modal ---- */}
          <Dialog
            open={Boolean(pendingTrend)}
            onClose={() => {
              if (adTimerRef.current) clearInterval(adTimerRef.current);
              setPendingTrend(null);
              setAdCountdown(0);
            }}
            maxWidth="sm"
            fullWidth
            BackdropProps={{
              sx: {
                backgroundColor: 'rgba(11, 17, 33, 0.85)',
                backdropFilter: 'blur(16px)'
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: { xs: 2, sm: 4 },
                m: { xs: 1, sm: 2 },
                boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
                bgcolor: '#0b1121',
                border: '1px solid rgba(232, 255, 0, 0.15)',
                overflow: 'hidden'
              }
            }}
          >
            {/* Top gradient bar */}
            <Box sx={{ height: '3px', background: 'linear-gradient(90deg, #e8ff00, #00c6ff, #e8ff00)', backgroundSize: '200% 100%', animation: 'adShimmer 2s linear infinite', '@keyframes adShimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } } }} />

            <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1, fontFamily: '"Poppins", sans-serif' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 2, mb: 0.5 }}>
                Sponsored
              </Typography>
              <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                Your prompt is loading...
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1, pt: 1 }}>
              {/* Google AdSense Ad Unit */}
              <Box
                sx={{
                  width: '100%',
                  minHeight: 250,
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  mb: 2
                }}
              >
                <ins
                  className="adsbygoogle"
                  ref={adRef}
                  style={{ display: 'block', width: '100%', minHeight: '250px' }}
                  data-ad-client="ca-pub-4098637668865010"
                  data-ad-slot="8830719426"
                  data-ad-format="auto"
                  data-full-width-responsive="true"
                />
              </Box>

              {/* Countdown Progress */}
              <Box sx={{ textAlign: 'center' }}>
                <LinearProgress
                  variant="determinate"
                  value={((5 - adCountdown) / 5) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mb: 1.5,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: 'linear-gradient(90deg, #e8ff00, #00c6ff)'
                    }
                  }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', fontFamily: '"Poppins", sans-serif', fontWeight: 500 }}>
                  {adCountdown > 0 ? (
                    <>Continue to prompt in <span style={{ color: '#e8ff00', fontWeight: 700 }}>{adCountdown}s</span></>
                  ) : (
                    <span style={{ color: '#25d366' }}>Opening prompt... ✓</span>
                  )}
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', pb: 2.5, pt: 1 }}>
              {adCountdown <= 3 && adCountdown > 0 && (
                <Button
                  onClick={handleSkipAd}
                  size="small"
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { color: '#e8ff00', bgcolor: 'rgba(232,255,0,0.05)' }
                  }}
                >
                  Skip Ad →
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* પ્રોમ્પ્ટ જોવા માટેનું મોડલ પોપઅપ */}
          <Dialog
            open={Boolean(selectedTrend)}
            onClose={() => setSelectedTrend(null)}
            maxWidth="md"
            fullWidth
            disableScrollLock={false}
            BackdropProps={{
              sx: {
                backgroundColor: 'rgba(11, 17, 33, 0.8)',
                backdropFilter: 'blur(12px)'
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: { xs: 2, sm: 4 },
                m: { xs: 1, sm: 2 },
                width: { xs: 'calc(100% - 8px)', sm: 'auto' },
                boxShadow: '0 20px 50px rgba(0,0,0,0.7)',
                bgcolor: '#0b1121', // Solid dark background for modal
                border: '1px solid rgba(255,255,255,0.1)'
              }
            }}
          >
            {selectedTrend && (
              <>
                <DialogTitle sx={{ fontWeight: '600', fontSize: { xs: '1.2rem', sm: '1.6rem' }, p: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 1 }, color: '#ffffff', fontFamily: '"Poppins", sans-serif' }}>
                  {selectedTrend.title}
                  <Typography variant="body2" sx={{ color: '#e8ff00', fontWeight: '600', display: 'inline-block', ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                    <Chip label={selectedTrend.category} size="small" sx={{ fontWeight: '600', bgcolor: 'rgba(232, 255, 0, 0.1)', color: '#e8ff00', border: '1px solid rgba(232,255,0,0.2)' }} />
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{
                  p: { xs: 1.5, sm: 3 },
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.02)', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(232, 255, 0, 0.5)' }
                }}>
                  <Grid container spacing={{ xs: 1.5, sm: 3 }} alignItems="flex-start">
                    {/* Image Section */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ position: 'relative', width: '100%', height: '100%', maxHeight: '70vh', borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {selectedTrend.beforeImageUrl && selectedTrend.afterImageUrl ? (
                          <Box sx={{ width: '100%', height: '100%', maxHeight: '70vh', '& img': { width: '100%', height: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' } }}>
                            <BeforeAfterSlider before={selectedTrend.beforeImageUrl} after={selectedTrend.afterImageUrl} />
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', maxHeight: '70vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={selectedTrend.afterImageUrl || selectedTrend.beforeImageUrl} alt={selectedTrend.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', backgroundColor: '#0b1121' }} />
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Content Section */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)', fontWeight: 300, fontFamily: '"Poppins", sans-serif', fontSize: '1.05rem' }}>
                        {selectedTrend.shortDescription}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#ffffff', fontWeight: '600', fontFamily: '"Poppins", sans-serif' }}>
                        Optimized AI Prompt:
                      </Typography>
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2.5 },
                          bgcolor: 'rgba(0,0,0,0.3)',
                          borderRadius: 3,
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          color: '#e8ff00',
                          whiteSpace: 'pre-wrap',
                          border: '1px solid rgba(255,255,255,0.1)',
                          flexGrow: 1,
                          overflowY: 'auto',
                          mb: 1,
                          boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.2)',
                          '&::-webkit-scrollbar': { width: '6px' },
                          '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: 'rgba(232, 255, 0, 0.3)', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(232, 255, 0, 0.8)' }
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedTrend.fullPrompt }}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                  <Button onClick={() => setSelectedTrend(null)} variant="outlined" sx={{
                    borderRadius: 1.5,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { borderColor: '#e8ff00', bgcolor: 'rgba(232, 255, 0, 0.05)', color: '#e8ff00' }
                  }}>
                    Close
                  </Button>

                  <Button
                    startIcon={copiedId === selectedTrend.id ? <CheckIcon /> : <ContentCopyIcon />}
                    onClick={() => handleCopyPrompt(selectedTrend.id, selectedTrend.fullPrompt)}
                    sx={{
                      borderRadius: 1.5,
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      fontFamily: '"Poppins", sans-serif',
                      px: 3,
                      bgcolor: copiedId === selectedTrend.id ? '#25d366' : '#e8ff00',
                      color: copiedId === selectedTrend.id ? 'white' : '#0b1121',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: copiedId === selectedTrend.id ? '#20b958' : '#d4e600',
                        transform: 'scale(1.02)'
                      }
                    }}
                  >
                    {copiedId === selectedTrend.id ? "Copied!" : "Copy Prompt"}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Our Clients & Testimonials Sections styled for AI Studio dark theme inside container */}
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)', pt: 4, mt: 6 }}>
            <OurClients theme="dark" />
          </Box>
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)', pt: 4, pb: 0 }}>
            <Testimonial theme="dark" />
          </Box>
        </div>
      </Box>

      <Footer name="AiContentStudio" />
    </Box>
  );
}
